// External libraries
import React from 'react';
import { useStoreState } from '@/store';

// Internal modules
import {
  features,
  getContent,
  getLocale,
  isGuestOnboarding,
  logger,
} from '@/lib';
import { NationalitySelect } from './NationalitySelect';
import { useMountLogging, useOnMount } from '@/lib/reactHooks';
import { ContextualAlertText, NationalityContainer } from './StyledComponents';
import { NationalitySelectBox } from './NationalitySelectBox';
import { once } from '@/lib/utils';

export type NationalityProps = {
  blurHandler: (e: React.FocusEvent<HTMLInputElement>) => void;
  changeHandler: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

// useOnMount fires again on every remount (not just re-renders), so once()
// keeps this to a single log per page load regardless of how many times
// Nationality mounts.
const logIlAmlNationalityExposure = once((): void => {
  logger.info('IS_IL_AML_ENABLED');
  logger.fpti('weasley_ilaml_nationality_rendered', {
    is_il_aml_enabled: true,
    state_name: 'signup',
    transition_name: 'ilaml_nationality_rendered',
  });
});

const Nationality = (props: NationalityProps): JSX.Element => {
  useMountLogging('Nationality');
  const hasAddress = features.isNotActive('isAddressless');
  const isILAMLEnabled = features.isActive('isILAMLEnabled');
  const shouldCollapseNationalitySelectBox = features.isActive(
    'shouldCollapseNationalitySelectBox',
  );
  const showSelectNationalityBox =
    hasAddress && (!shouldCollapseNationalitySelectBox || isILAMLEnabled);

  useOnMount(() => {
    if (isILAMLEnabled && !isGuestOnboarding()) {
      logIlAmlNationalityExposure();
    }
  });

  const isFormSubmitting = useStoreState(({ ui }) => ui.isFormSubmitting);
  const selectOptions = useStoreState(({ api }) => {
    const { locale } = getLocale();
    return api.griffinMetadata?.territories[locale];
  });

  if (showSelectNationalityBox) {
    return (
      <>
        <NationalityContainer isFirstField={!hasAddress}>
          <NationalitySelectBox
            // @ts-expect-error disabled doesn't exist
            disabled={isFormSubmitting}
            selectOptions={selectOptions}
            shouldFocusOnRender={!hasAddress}
            {...props}
          />
        </NationalityContainer>
        {isILAMLEnabled && (
          <ContextualAlertText id="ilAmlContextualAlert">
            {getContent('simplifiedSectionHeaders.guest.ilaml.contextualAlert')}
          </ContextualAlertText>
        )}
      </>
    );
  }

  return (
    <NationalityContainer isFirstField={!hasAddress}>
      <NationalitySelect />
    </NationalityContainer>
  );
};

export { Nationality };
